import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect, Fragment } from "react";
import { ManageBankFeedsLink } from "./ManageBankFeedsLink";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, ChevronDown, Undo2, X, FileArchive, Search, Scissors, EyeOff, Eye, CheckCircle2, MoreHorizontal } from "lucide-react";
import type { ExpenseCategorySettings } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateRangeFilter, computeDateRange, type DatePreset , ALL_DATE_PRESETS } from "@/components/DateRangeFilter";
import { parsePadsplitBreakdown } from "@/lib/pnl-profit";
import { highlightMatch, IncomeRow, INCOME_CATEGORIES, formatMercuryAccountLabel } from "./_shared";
import { sortPropertiesParentFirst } from "@/lib/pnl-columns";
import { PropertyFilterSelect, CategoryFilterSelect } from "./_filters";
import { StatusTabs, deriveReviewStatus, type ReviewStatusFilter } from "./_StatusTabs";
import { FinancialsToolbar } from "./_FinancialsToolbar";
import { BulkBar } from "./_BulkBar";
import { InlineCellEditor } from "./_InlineCellEditor";
import { useRangeSelection } from "@/hooks/use-range-selection";



function incomeSourceBadge(source: string) {
  if (source === "stripe_bank") {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-800">Stripe bank feed</span>;
  }
  if (source === "mercury_credit") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
        Mercury
      </span>
    );
  }
  if (source === "padsplit_earnings") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        PadSplit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      Manual
    </span>
  );
}

function reconciliationStatusBadge(status: string) {
  if (status === "matched") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        Matched
      </span>
    );
  }
  if (status === "partially_matched" || status === "partial") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      Unmatched
    </span>
  );
}

export function IncomeTab({ properties }: { properties: Array<{ id: number; address: string | null; status?: string | null }> }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  // Task #2301 — Fetch category settings to merge custom income categories and
  // respect disabled income categories in the Add Income form dropdown.
  const incomeCatSettingsQuery = useQuery<ExpenseCategorySettings>({ queryKey: ["/api/app/expense-category-settings"] });
  const allIncomeCategories = useMemo(() => {
    const custom = ((incomeCatSettingsQuery.data as any)?.customIncomeCategories as {label: string; value: string}[]) || [];
    const disabled = ((incomeCatSettingsQuery.data as any)?.disabledIncomeCategories as string[]) || [];
    const builtIn = INCOME_CATEGORIES.filter((c) => !disabled.includes(c.value));
    return [...builtIn, ...custom];
  }, [incomeCatSettingsQuery.data]);
  const searchParams = new URLSearchParams(search);

  // ── Filters (URL-persisted, prefixed with "inc_" to avoid collision with Expenses params) ─
  const VALID_INCOME_SOURCES = ["mercury_credit", "padsplit_earnings", "manual", "stripe_bank"] as const;
  const VALID_DATE_PRESETS: readonly DatePreset[] = ALL_DATE_PRESETS;

  const filterPropertyId = searchParams.get("inc_propertyIds") ?? "";
  const rawIncDatePreset = searchParams.get("inc_datePreset") || "";
  const filterDatePreset = (VALID_DATE_PRESETS.includes(rawIncDatePreset as DatePreset) ? rawIncDatePreset : "") as DatePreset;
  const filterDateFrom = searchParams.get("inc_dateFrom") || "";
  const filterDateTo = searchParams.get("inc_dateTo") || "";
  const rawIncSource = searchParams.get("inc_source") || "";
  const filterSource = (VALID_INCOME_SOURCES.includes(rawIncSource as any) ? rawIncSource : "") as "" | "mercury_credit" | "padsplit_earnings" | "manual" | "stripe_bank";
  const rawIncBatchId = searchParams.get("inc_batchId") || "";
  const filterImportBatchId = (rawIncBatchId && /^\d+$/.test(rawIncBatchId)) ? Number(rawIncBatchId) : null;
  const filterCategory = searchParams.get("inc_category") || "";
  // Revenue triage — QBO-style status buckets (For review / Categorized /
  // Excluded / All). Persisted in the URL as `inc_status`; default "for_review".
  const VALID_STATUS_FILTERS: readonly ReviewStatusFilter[] = ["for_review", "categorized", "excluded", "all"];
  const rawIncStatus = searchParams.get("inc_status") || "";
  const statusFilter: ReviewStatusFilter = VALID_STATUS_FILTERS.includes(rawIncStatus as ReviewStatusFilter)
    ? (rawIncStatus as ReviewStatusFilter)
    : "for_review";

  const updateIncomeQuery = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const setFilterPropertyId = (id: string) => updateIncomeQuery({ inc_propertyIds: id || null });
  const setFilterSource = (v: "" | "mercury_credit" | "padsplit_earnings" | "manual" | "stripe_bank") => updateIncomeQuery({ inc_source: v || null });
  const setFilterImportBatchId = (id: number | null) => updateIncomeQuery({ inc_batchId: id != null ? String(id) : null });
  const setIncomeDatePreset = (preset: DatePreset) => updateIncomeQuery({ inc_datePreset: preset || null, inc_dateFrom: null, inc_dateTo: null });
  const setIncomeDateFrom = (v: string) => updateIncomeQuery({ inc_dateFrom: v || null });
  const setIncomeDateTo = (v: string) => updateIncomeQuery({ inc_dateTo: v || null });
  const setFilterCategory = (v: string) => updateIncomeQuery({ inc_category: v || null });
  const setStatusFilter = (v: ReviewStatusFilter) => updateIncomeQuery({ inc_status: v === "for_review" ? null : v });

  const [incomeSearchText, setIncomeSearchText] = useListState("income:search", "");
  const [selectedIncomeIds, setSelectedIncomeIds] = useState<Set<number>>(new Set());

  // Switching status tabs swaps out the visible row set — drop the selection so
  // the bulk bar can't act on rows the host can no longer see.
  useEffect(() => {
    setSelectedIncomeIds(new Set());
  }, [statusFilter]);

  // ── Modal / drawer state ─────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<IncomeRow | null>(null);
  const [selectedEarningsIds, setSelectedEarningsIds] = useState<Set<number>>(new Set());
  const [showMatchPanel, setShowMatchPanel] = useState(false);
  const [editingRow, setEditingRow] = useState<IncomeRow | null>(null);

  type ImportHistoryEntry = {
    id: number;
    importedAt: string;
    dateRangeStart: string | null;
    dateRangeEnd: string | null;
    propertiesMatched: number;
    rowsInserted: number;
    rawSummary?: {
      producer?: string;
      entries?: Array<{ propertyId?: number; month?: string }>;
      notices?: string[];
    } | null;
  };
  const { data: importHistoryData } = useQuery<{ latest: ImportHistoryEntry | null; imports: ImportHistoryEntry[] }>({
    queryKey: ["/api/app/financials/padsplit-import/history"],
  });
  const importHistory = importHistoryData?.latest ?? null;
  const importHistoryList = importHistoryData?.imports ?? [];
  const [showImportHistoryPopover, setShowImportHistoryPopover] = useState(false);

  // ── Add/edit form ────────────────────────────────────────────────────────
  const [addForm, setAddForm] = useState<{
    propertyId: string;
    category: string;
    amount: string;
    date: string;
    vendor: string;
    description: string;
    notes: string;
  }>({ propertyId: "", category: "other_income", amount: "", date: new Date().toISOString().substring(0, 10), vendor: "", description: "", notes: "" });

  // Task #2305 — income split mode state
  const [incomeSplitMode, setIncomeSplitMode] = useState(false);
  const [incomeSplitLines, setIncomeSplitLines] = useState<Array<{ propertyId: string; category: string; amount: string; notes: string }>>([]);
  const [incomeSplitError, setIncomeSplitError] = useState<string | null>(null);
  // Task #3788 — client-side filter by Mercury account (last 4).
  const [accountFilter, setAccountFilter] = useListState<string>("income:account", "all");

  function resetAddForm() {
    setAddForm({ propertyId: "", category: "other_income", amount: "", date: new Date().toISOString().substring(0, 10), vendor: "", description: "", notes: "" });
    setEditingRow(null);
    setIncomeSplitMode(false);
    setIncomeSplitLines([]);
    setIncomeSplitError(null);
  }

  function openEdit(row: IncomeRow) {
    setEditingRow(row);
    setAddForm({
      // Task #3788 — map the durable company-level marker back to the sentinel.
      propertyId: row.isCompanyLevel ? "__company__" : row.propertyId ? String(row.propertyId) : "",
      category: row.category || "other_income",
      amount: row.amount,
      date: row.date,
      vendor: row.vendor || "",
      description: row.description || "",
      notes: row.notes || "",
    });
    // Task #2305 — load split children when editing a split parent income row
    const children = rows.filter((r) => (r as any).splitParentId === row.id);
    if (children.length > 0) {
      setIncomeSplitMode(true);
      setIncomeSplitLines(children.map((c) => ({
        // Task #3788 — map a company-level child back to the sentinel so the
        // split-line picker shows "Company Operations".
        propertyId: c.isCompanyLevel ? "__company__" : c.propertyId ? String(c.propertyId) : "",
        category: c.category || "other_income",
        amount: c.amount,
        notes: c.notes || "",
      })));
    } else {
      setIncomeSplitMode(false);
      setIncomeSplitLines([]);
    }
    setShowAddModal(true);
  }

  // ── Query ────────────────────────────────────────────────────────────────
  const incomeDateRange = useMemo(
    () => computeDateRange(filterDatePreset, filterDateFrom, filterDateTo),
    [filterDatePreset, filterDateFrom, filterDateTo],
  );
  const queryParams = new URLSearchParams();
  // Property filter is applied client-side (with parent→child rollup) so we
  // don't send propertyIds to the backend. All other filters remain server-side.
  if (incomeDateRange.from) queryParams.set("dateFrom", incomeDateRange.from);
  if (incomeDateRange.to) queryParams.set("dateTo", incomeDateRange.to);
  if (filterSource) queryParams.set("source", filterSource);
  if (filterImportBatchId != null) queryParams.set("importBatchId", String(filterImportBatchId));
  // Always fetch excluded rows — the status tabs bucket them client-side into
  // the "Excluded" tab (there is no separate "show excluded" toggle anymore).
  queryParams.set("includeExcluded", "true");
  const queryString = queryParams.toString();

  const { data: rows = [], isLoading } = useQuery<IncomeRow[]>({
    queryKey: ["/api/app/income", queryString],
    queryFn: async () => {
      const res = await fetch(`/api/app/income${queryString ? `?${queryString}` : ""}`);
      if (!res.ok) throw new Error("Failed to load income");
      return res.json();
    },
  });

  // Task #2305 — build split maps for income tab
  const incomeSplitChildrenMap = useMemo(() => {
    const m = new Map<number, IncomeRow[]>();
    for (const r of rows) {
      const pid = (r as any).splitParentId;
      if (pid != null) {
        if (!m.has(pid)) m.set(pid, []);
        m.get(pid)!.push(r);
      }
    }
    return m;
  }, [rows]);

  const incomeSplitParentIds = useMemo(() => new Set(incomeSplitChildrenMap.keys()), [incomeSplitChildrenMap]);

  // Task #3788 — distinct Mercury accounts (by last 4) for the account filter.
  const incomeAccountOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      const last4 = r.accountLast4;
      if (!last4) continue;
      map.set(last4, r.accountNickname ? `${r.accountNickname} · ${last4}` : `•••• ${last4}`);
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // All non-child rows matching the search/property/date/source/category/account
  // filters, across every status bucket. The status tabs slice this AFTER the
  // other filters so the per-tab counts stay consistent with what's filtered.
  const filteredIncomeRows = useMemo(() => {
    // Exclude split children from standalone rows
    let nonChildren = rows.filter((r) => (r as any).splitParentId == null);
    // Property filter with parent→child rollup (matching Expenses' behavior).
    // Client-side so selecting a parent includes all child-unit rows.
    if (filterPropertyId) {
      const selectedId = parseInt(filterPropertyId);
      const childIds = properties.filter((p: any) => p.parentPropertyId === selectedId).map((p: any) => p.id);
      const allIds = new Set([selectedId, ...childIds]);
      nonChildren = nonChildren.filter((r) => {
        if ((r as any).isCompanyLevel) return false;
        return r.propertyId !== null && allIds.has(r.propertyId);
      });
    }
    // Task #3788 — account (last 4) filter.
    if (accountFilter !== "all") {
      nonChildren = nonChildren.filter((r) => r.accountLast4 === accountFilter);
    }
    // Category filter
    if (filterCategory) {
      nonChildren = nonChildren.filter((r) => r.category === filterCategory);
    }
    const q = incomeSearchText.trim().toLowerCase();
    if (!q) return nonChildren;
    return nonChildren.filter((r) => {
      const desc = (r.description || "").toLowerCase();
      const vendor = (r.vendor || "").toLowerCase();
      const children = incomeSplitChildrenMap.get(r.id) ?? [];
      const childMatch = children.some((c) =>
        String(c.description ?? "").toLowerCase().includes(q) ||
        String(c.vendor ?? "").toLowerCase().includes(q)
      );
      return desc.includes(q) || vendor.includes(q) || childMatch;
    });
  }, [rows, incomeSearchText, incomeSplitChildrenMap, accountFilter, filterCategory, filterPropertyId, properties]);

  // Revenue triage — bucket counts + the rows for the active status tab.
  const statusCounts = useMemo<Record<ReviewStatusFilter, number>>(() => {
    const counts: Record<ReviewStatusFilter, number> = { for_review: 0, categorized: 0, excluded: 0, all: 0 };
    for (const r of filteredIncomeRows) {
      counts[deriveReviewStatus(r)] += 1;
      counts.all += 1;
    }
    return counts;
  }, [filteredIncomeRows]);

  const displayedIncomeRows = useMemo(
    () => (statusFilter === "all" ? filteredIncomeRows : filteredIncomeRows.filter((r) => deriveReviewStatus(r) === statusFilter)),
    [filteredIncomeRows, statusFilter],
  );

  const displayedIncomeIds = useMemo(() => displayedIncomeRows.map((r) => r.id), [displayedIncomeRows]);
  const { handleItemClick: handleIncomeRowClick } = useRangeSelection(
    displayedIncomeIds,
    selectedIncomeIds,
    setSelectedIncomeIds,
    `${statusFilter}|${queryString}|${filterPropertyId}|${filterCategory}|${accountFilter}|${incomeSearchText}`,
  );

  // ── Suggest matches query ─────────────────────────────────────────────────
  const { data: suggestedMatches = [], isLoading: suggestLoading } = useQuery<(IncomeRow & { score: number; amountDiff: number; dateDiff: number; hasSplits?: boolean })[]>({
    queryKey: ["/api/app/income", selectedDeposit?.id, "suggest-matches"],
    queryFn: async () => {
      if (!selectedDeposit) return [];
      const res = await fetch(`/api/app/income/${selectedDeposit.id}/suggest-matches`);
      if (!res.ok) throw new Error("Failed to load suggestions");
      return res.json();
    },
    enabled: !!selectedDeposit && showMatchPanel,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  // Task #2305 — income split mutations
  const upsertIncomeSplitMutation = useMutation({
    mutationFn: async ({ id, lines }: { id: number; lines: Array<{ propertyId: number | null; isCompanyLevel: boolean; category: string; amount: string; notes: string | null }> }) => {
      const res = await apiRequest("POST", `/api/app/income/${id}/splits`, { lines });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
    },
    onError: () => toast({ title: "Failed to save split lines", variant: "destructive" }),
  });

  const deleteIncomeSplitMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/app/income/${id}/splits`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
    },
  });

  // Task #2314 — confirmation guard when host toggles Split OFF on income row
  // and saves, so the existing split line items aren't silently removed.
  const [incomeUnsplitConfirm, setIncomeUnsplitConfirm] = useState<{ childCount: number } | null>(null);

  // Task #2314 — Authoritative child count for the editing row, fetched
  // unfiltered from the server. The cached `/api/app/income` list filters by
  // property/date, so its children map can undercount and let an accidental
  // unsplit slip through. This query is the source of truth for the dialog.
  const editingChildCountQuery = useQuery<{ children: Array<{ id: number }> }>({
    queryKey: ["/api/app/income", editingRow?.id, "splits"],
    enabled: !!editingRow,
  });
  const editingActualChildCount = editingChildCountQuery.data?.children?.length ?? 0;
  // Pessimistically treat an unresolved OR failed query as "may have
  // children" so a host can't slip a save through before the count loads
  // (or after a transient error). Only a successful response unlocks save.
  const editingChildCountUnresolved = !!editingRow && !editingChildCountQuery.isSuccess;

  const addMutation = useMutation({
    mutationFn: async (data: typeof addForm) => {
      const endpoint = editingRow ? `/api/app/income/${editingRow.id}` : "/api/app/income";
      const method = editingRow ? "PATCH" : "POST";
      // Task #3788 — "__company__" sentinel marks a company-level (non-property)
      // transaction; it forces propertyId null and sets the durable marker.
      const isCompanyLevel = data.propertyId === "__company__";
      const res = await apiRequest(method, endpoint, {
        propertyId: isCompanyLevel ? null : data.propertyId ? parseInt(data.propertyId) : null,
        isCompanyLevel,
        category: data.category,
        amount: data.amount,
        date: data.date,
        vendor: data.vendor || null,
        description: data.description || null,
        notes: data.notes || null,
      });
      return res.json();
    },
    onSuccess: async (savedRow) => {
      // Task #2305 — save or clear split lines after income row save
      const rowId = editingRow?.id ?? savedRow?.id;
      if (rowId) {
        if (incomeSplitMode && incomeSplitLines.length >= 2) {
          // Task #3788 — normalize each split line's property selection: the
          // "__company__" sentinel must never reach the integer propertyId
          // column. Map it to { propertyId: null, isCompanyLevel: true }.
          const apiLines = incomeSplitLines.map((l) => {
            const lineCompany = l.propertyId === "__company__";
            return {
              propertyId: lineCompany ? null : l.propertyId ? parseInt(l.propertyId) : null,
              isCompanyLevel: lineCompany,
              category: l.category,
              amount: l.amount,
              notes: l.notes || null,
            };
          });
          await upsertIncomeSplitMutation.mutateAsync({ id: rowId, lines: apiLines });
        } else if (!incomeSplitMode) {
          await deleteIncomeSplitMutation.mutateAsync(rowId);
        }
      }
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setShowAddModal(false);
      resetAddForm();
      toast({ title: editingRow ? "Income updated" : "Income added" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/app/income/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      toast({ title: "Income deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const updateIncomeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/app/income/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  const excludeIncomeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/income/${id}/exclude`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      toast({ title: "Income excluded from P&L" });
    },
    onError: () => toast({ title: "Failed to exclude", variant: "destructive" }),
  });

  const unexcludeIncomeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/income/${id}/unexclude`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      toast({ title: "Income restored" });
    },
    onError: () => toast({ title: "Failed to restore", variant: "destructive" }),
  });

  const bulkExcludeIncomeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (ids: number[]) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-exclude", { ids });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setSelectedIncomeIds(new Set());
      toast({ title: "Selected income excluded from P&L" });
    },
    onError: () => toast({ title: "Failed to bulk exclude", variant: "destructive" }),
  });

  const bulkUnexcludeIncomeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (ids: number[]) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-unexclude", { ids });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setSelectedIncomeIds(new Set());
      toast({ title: "Selected income restored" });
    },
    onError: () => toast({ title: "Failed to bulk restore", variant: "destructive" }),
  });

  const bulkSetCategoryMutation = useMutation({
    mutationFn: async ({ ids, category }: { ids: number[]; category: string | null }) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-set-category", { ids, category });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setSelectedIncomeIds(new Set());
      toast({ title: "Category set on selected income" });
    },
    onError: () => toast({ title: "Failed to set category", variant: "destructive" }),
  });

  const bulkSetPropertyMutation = useMutation({
    mutationFn: async ({ ids, propertyValue }: { ids: number[]; propertyValue: string | number | null }) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-set-property", { ids, propertyValue });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setSelectedIncomeIds(new Set());
      toast({ title: "Property set on selected income" });
    },
    onError: () => toast({ title: "Failed to set property", variant: "destructive" }),
  });

  // Revenue triage — stamp/clear the human-confirmation `reviewedAt` on one or
  // many rows. A single row's green check posts here with ids:[id].
  const bulkSetReviewedMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ ids, reviewed }: { ids: number[]; reviewed: boolean }) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-set-reviewed", { ids, reviewed });
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setSelectedIncomeIds(new Set());
      toast({
        title: vars.reviewed
          ? `Marked ${vars.ids.length} ${vars.ids.length === 1 ? "row" : "rows"} reviewed`
          : `Moved ${vars.ids.length} ${vars.ids.length === 1 ? "row" : "rows"} back to For review`,
      });
    },
    onError: () => toast({ title: "Failed to update review status", variant: "destructive" }),
  });

  const matchMutation = useMutation({
    mutationFn: async ({ depositId, earningsIds }: { depositId: number; earningsIds: number[] }) => {
      const res = await apiRequest("POST", `/api/app/income/${depositId}/match`, { earningsIncomeIds: earningsIds });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setShowMatchPanel(false);
      setSelectedDeposit(null);
      setSelectedEarningsIds(new Set());
      toast({ title: `Matched — variance: $${Math.abs(parseFloat(data.variance ?? "0")).toFixed(2)}` });
    },
    onError: () => toast({ title: "Failed to match", variant: "destructive" }),
  });

  const unmatchMutation = useMutation({
    mutationFn: async (depositId: number) => {
      const res = await apiRequest("DELETE", `/api/app/income/${depositId}/match`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      toast({ title: "Unmatched" });
    },
    onError: () => toast({ title: "Failed to unmatch", variant: "destructive" }),
  });

  const [lastAutoMatch, setLastAutoMatch] = useState<{ depositIds: number[]; at: number } | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    if (!lastAutoMatch) return;
    const t = setInterval(() => setNowTs(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [lastAutoMatch]);
  const UNDO_WINDOW_MS = 5 * 60 * 1000;
  const undoSecondsLeft = lastAutoMatch
    ? Math.max(0, Math.ceil((lastAutoMatch.at + UNDO_WINDOW_MS - nowTs) / 1000))
    : 0;
  const canUndo = lastAutoMatch !== null && lastAutoMatch.depositIds.length > 0 && undoSecondsLeft > 0;

  const bulkAutoMatchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/income/bulk-auto-match");
      return res.json() as Promise<{
        ok: boolean;
        totalUnmatched: number;
        matched: number;
        skippedAmbiguous: number;
        skippedNoMatch: number;
        matchedDepositIds: number[];
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      const skipped = data.skippedAmbiguous + data.skippedNoMatch;
      const ambiguousNote = data.skippedAmbiguous > 0
        ? ` (${data.skippedAmbiguous} ambiguous)`
        : "";
      toast({
        title: `${data.matched} deposit${data.matched === 1 ? "" : "s"} matched, ${skipped} skipped${ambiguousNote} — review below`,
      });
      if (data.matched > 0 && Array.isArray(data.matchedDepositIds) && data.matchedDepositIds.length > 0) {
        setLastAutoMatch({ depositIds: data.matchedDepositIds, at: Date.now() });
        setNowTs(Date.now());
      } else {
        setLastAutoMatch(null);
      }
    },
    onError: () => toast({ title: "Auto-match failed", variant: "destructive" }),
  });

  const undoAutoMatchMutation = useMutation({
    mutationFn: async (depositIds: number[]) => {
      const res = await apiRequest("POST", "/api/app/income/bulk-unmatch", { depositIds });
      return res.json() as Promise<{ ok: boolean; unmatched: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      setLastAutoMatch(null);
      toast({
        title: `Reverted ${data.unmatched} deposit${data.unmatched === 1 ? "" : "s"}`,
      });
    },
    onError: () => toast({ title: "Undo failed", variant: "destructive" }),
  });

  // ── Computed totals ───────────────────────────────────────────────────────
  const totalMercury = rows.filter((r) => r.source === "mercury_credit").reduce((s, r) => s + parseFloat(r.amount), 0);
  const totalPadSplit = rows.filter((r) => r.source === "padsplit_earnings").reduce((s, r) => s + parseFloat(r.amount), 0);
  const totalManual = rows.filter((r) => r.source === "manual").reduce((s, r) => s + parseFloat(r.amount), 0);
  const totalUnmatched = rows.filter((r) => r.source === "mercury_credit" && r.reconciliationStatus === "unmatched").length;

  const propertyMap = Object.fromEntries(properties.map((p) => [p.id, p.address]));

  // ── Match panel: available earnings rows ─────────────────────────────────
  const earningsRows = rows.filter((r) => r.source === "padsplit_earnings" && r.reconciliationStatus === "unmatched");
  const matchPanelEarnings = showMatchPanel ? [...new Map([...suggestedMatches, ...earningsRows].map((r) => [r.id, r])).values()] : [];

  // Task #2285 — Client-side Income CSV export with a "Breakdown" column
  // populated from the structured `notes` field on padsplit_earnings rows
  // (parsed by parsePadsplitBreakdown). Honors the active filters AND the
  // active status tab via the already-filtered displayedIncomeRows list
  // (excluded rows are only exported from the Excluded / All tabs).
  const handleExportIncomeCsv = () => {
    const csvEscape = (val: unknown): string => {
      if (val == null) return "";
      const s = String(val).replace(/\r\n|\r|\n/g, " ");
      if (/[",]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const formatBreakdown = (row: IncomeRow): string => {
      if (row.source !== "padsplit_earnings") return "";
      const bd = parsePadsplitBreakdown(row.notes);
      if (!bd) return "";
      const parts: string[] = [`Dues: $${bd.dues.toFixed(2)}`];
      if (bd.lateFees !== 0) parts.push(`Late: $${bd.lateFees.toFixed(2)}`);
      if (bd.bookingFees !== 0) parts.push(`Booking: -$${bd.bookingFees.toFixed(2)}`);
      if (bd.serviceFees !== 0) parts.push(`Service: -$${bd.serviceFees.toFixed(2)}`);
      return parts.join("; ");
    };
    const categoryLabel = (value: string | null | undefined): string =>
      allIncomeCategories.find((c) => c.value === value)?.label ?? (value || "");
    const propertyMapLocal: Record<number, string> = {};
    for (const p of properties) {
      propertyMapLocal[p.id] = p.address || `Property #${p.id}`;
    }
    // Column order mirrors the on-screen Income tab (Date, Source, Property,
    // Account, Category, Description, Amount, Status), with Vendor and
    // Breakdown retained as trailing detail columns.
    const header = ["Date", "Source", "Property", "Account", "Category", "Vendor", "Description", "Amount", "Status", "Breakdown"];
    const lines: string[] = [header.map(csvEscape).join(",")];
    for (const row of displayedIncomeRows) {
      lines.push([
        row.date,
        row.source,
        row.isCompanyLevel ? "Company Operations" : row.propertyId ? (propertyMapLocal[row.propertyId] || `Property #${row.propertyId}`) : "",
        formatMercuryAccountLabel(row.accountNickname, row.accountLast4) ?? "",
        categoryLabel(row.category),
        row.vendor || "",
        row.description || "",
        parseFloat(row.amount || "0").toFixed(2),
        row.reconciliationStatus || "",
        formatBreakdown(row),
      ].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `income-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Inline Property cell options (shared by every row's InlineCellEditor).
  const incomePropertyOptions = useMemo(() => {
    const opts = [
      { value: "none", label: "No property" },
      { value: "__company__", label: "Company Operations" },
    ];
    for (const p of sortPropertiesParentFirst(properties.filter((prop) => prop.status !== "archive") as any) as any[]) {
      opts.push({
        value: String(p.id),
        label: p.parentPropertyId ? `└ ${p.address || p.name || `#${p.id}`}` : (p.address || p.name || `Property #${p.id}`),
      });
    }
    return opts;
  }, [properties]);

  const incomeCategoryOptions = useMemo(
    () => [{ value: "none", label: "No category" }, ...allIncomeCategories],
    [allIncomeCategories],
  );

  // Advanced (popover) filters: source, category, Mercury account, import batch.
  const activeAdvancedFilterCount =
    (filterSource ? 1 : 0) +
    (filterCategory ? 1 : 0) +
    (accountFilter !== "all" ? 1 : 0) +
    (filterImportBatchId != null ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "mercury", label: "Mercury Credits", value: totalMercury, color: "text-blue-600" },
          { key: "padsplit", label: "PadSplit Earnings", value: totalPadSplit, color: "text-green-600" },
          { key: "manual", label: "Manual Entries", value: totalManual, color: "text-gray-700" },
          { key: "unmatched", label: "Unmatched Deposits", value: totalUnmatched, isCount: true, color: totalUnmatched > 0 ? "text-amber-600" : "text-gray-500" },
        ].map((card) => {
          const isPadSplit = card.key === "padsplit";
          const showImportBadge = isPadSplit && importHistory;
          const importRange = showImportBadge
            ? (importHistory!.dateRangeStart && importHistory!.dateRangeEnd
                ? (importHistory!.dateRangeStart === importHistory!.dateRangeEnd
                    ? importHistory!.dateRangeStart
                    : `${importHistory!.dateRangeStart} – ${importHistory!.dateRangeEnd}`)
                : (importHistory!.dateRangeStart ?? importHistory!.dateRangeEnd ?? null))
            : null;
          return (
            <div key={card.label} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`card-income-summary-${card.key}`}>
              <p className="text-xs text-gray-500 mb-1">{card.label}</p>
              <p className={`text-xl font-semibold ${card.color}`}>
                {card.isCount ? card.value : `$${(card.value as number).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </p>
              {showImportBadge && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setFilterSource("padsplit_earnings")}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                      filterSource === "padsplit_earnings"
                        ? "border-green-300 dark:border-green-700 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200"
                        : "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50"
                    }`}
                    title={`Last update: ${new Date(importHistory!.importedAt).toLocaleString()} · ${importHistory!.rowsInserted ?? 0} changed month${(importHistory!.rowsInserted ?? 0) === 1 ? "" : "s"}. Click to filter table.`}
                    data-testid="badge-padsplit-last-import"
                  >
                    <FileArchive className="h-3 w-3" />
                    <span>
                      Last update{importRange ? `: ${importRange}` : ""} · {importHistory!.rowsInserted ?? 0} changed month{(importHistory!.rowsInserted ?? 0) === 1 ? "" : "s"}
                    </span>
                  </button>
                  {importHistoryList.length > 0 && (
                    <Popover open={showImportHistoryPopover} onOpenChange={setShowImportHistoryPopover}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50 transition-colors"
                          title="View past PadSplit imports"
                          data-testid="button-padsplit-import-history"
                        >
                          <span>History ({importHistoryList.length})</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-[420px] max-w-[calc(100vw-2rem)] p-0" data-testid="popover-padsplit-import-history">
                        <div className="border-b border-gray-100 px-3 py-2">
                          <p className="text-xs font-medium text-gray-700">PadSplit earnings history</p>
                          <p className="text-[11px] text-gray-500">Click an update to filter the table below.</p>
                        </div>
                        <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                          {importHistoryList.map((entry, idx) => {
                            const range = entry.dateRangeStart && entry.dateRangeEnd
                              ? (entry.dateRangeStart === entry.dateRangeEnd
                                  ? entry.dateRangeStart
                                  : `${entry.dateRangeStart} – ${entry.dateRangeEnd}`)
                              : (entry.dateRangeStart ?? entry.dateRangeEnd ?? null);
                            const isActive = filterImportBatchId === entry.id;
                            return (
                              <li key={entry.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFilterImportBatchId(isActive ? null : entry.id);
                                    setShowImportHistoryPopover(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                    isActive ? "bg-green-50 dark:bg-green-950/40" : "hover:bg-gray-50"
                                  }`}
                                  data-testid={`button-padsplit-import-history-entry-${entry.id}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-gray-800">
                                      {new Date(entry.importedAt).toLocaleString()}
                                      {entry.rawSummary?.producer === "browser_harvest" && (
                                        <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300">
                                          Daily harvest
                                        </span>
                                      )}
                                      {idx === 0 && (
                                        <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-300">
                                          Latest
                                        </span>
                                      )}
                                    </span>
                                    {isActive && (
                                      <span className="text-[10px] font-medium text-green-700">Filtering</span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-gray-500 mt-0.5">
                                    {range ? `${range} · ` : ""}
                                    {entry.rawSummary?.producer === "browser_harvest"
                                      ? `${entry.rowsInserted ?? 0} changed month${(entry.rowsInserted ?? 0) === 1 ? "" : "s"}`
                                      : `${entry.rowsInserted ?? 0} imported row${(entry.rowsInserted ?? 0) === 1 ? "" : "s"}`}
                                    {" · "}{entry.propertiesMatched ?? 0} {(entry.propertiesMatched ?? 0) === 1 ? "property" : "properties"}
                                  </div>
                                  {Array.isArray(entry.rawSummary?.notices) && entry.rawSummary.notices.map((notice, noticeIndex) => (
                                    <p
                                      key={`${entry.id}-notice-${noticeIndex}`}
                                      className="mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                                      data-testid={`notice-padsplit-import-history-${entry.id}-${noticeIndex}`}
                                    >
                                      {notice}
                                    </p>
                                  ))}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        {filterImportBatchId != null && (
                          <div className="border-t border-gray-100 px-3 py-2">
                            <button
                              type="button"
                              onClick={() => {
                                setFilterImportBatchId(null);
                                setShowImportHistoryPopover(false);
                              }}
                              className="text-[11px] text-blue-600 hover:underline"
                              data-testid="button-padsplit-import-history-clear"
                            >
                              Clear import batch filter
                            </button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Revenue triage — status tabs (For review / Categorized / Excluded / All) */}
      <ManageBankFeedsLink />
      <StatusTabs value={statusFilter} onChange={setStatusFilter} counts={statusCounts} idPrefix="income" />

      {/* Toolbar — primary filters, Filters popover, Add Income + overflow menu */}
      <FinancialsToolbar
        idPrefix="income"
        primaryFilters={
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={incomeSearchText}
                onChange={(e) => setIncomeSearchText(e.target.value)}
                placeholder="Search description or vendor…"
                className="pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
                data-testid="input-search-income"
              />
              {incomeSearchText && (
                <button
                  type="button"
                  onClick={() => setIncomeSearchText("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="button-clear-search-income"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <PropertyFilterSelect
              value={filterPropertyId}
              onValueChange={setFilterPropertyId}
              properties={properties as any}
              testId="select-income-property-filter"
              placeholder="All Properties"
            />
            <DateRangeFilter
              preset={filterDatePreset}
              customFrom={filterDateFrom}
              customTo={filterDateTo}
              onPresetChange={setIncomeDatePreset}
              onCustomFromChange={setIncomeDateFrom}
              onCustomToChange={setIncomeDateTo}
              triggerClassName="border border-gray-200 rounded-md px-3 py-1.5 text-sm bg-white h-9 w-40"
            />
          </>
        }
        advancedFilters={
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Source</label>
              <Select value={filterSource || "all"} onValueChange={(v) => setFilterSource(v === "all" ? "" : v as any)}>
                <SelectTrigger className="w-full text-sm" data-testid="select-income-source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="mercury_credit">Mercury Credits</SelectItem>
                  <SelectItem value="stripe_bank">Stripe bank feeds</SelectItem>
                  <SelectItem value="padsplit_earnings">PadSplit Earnings</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Category</label>
              <CategoryFilterSelect
                value={filterCategory}
                onValueChange={setFilterCategory}
                categories={allIncomeCategories}
                testId="select-income-category-filter"
                placeholder="All Categories"
                className="w-full text-sm"
              />
            </div>
            {/* Task #3788 — filter by Mercury account (last 4). */}
            {incomeAccountOptions.length > 0 && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Mercury account</label>
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="w-full text-sm" data-testid="select-income-account-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {incomeAccountOptions.map((a) => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        }
        activeFilterCount={activeAdvancedFilterCount}
        onClearFilters={() => {
          updateIncomeQuery({ inc_source: null, inc_category: null, inc_batchId: null });
          setAccountFilter("all");
        }}
        primaryAction={
          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetAddForm(); setShowAddModal(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              data-testid="button-add-income"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Income
            </button>
          </div>
        }
        overflowItems={
          <>
            <DropdownMenuItem onClick={handleExportIncomeCsv} data-testid="button-export-income-csv">
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </DropdownMenuItem>
            {(totalUnmatched > 0 || (canUndo && lastAutoMatch)) && <DropdownMenuSeparator />}
            {totalUnmatched > 0 && (
              <DropdownMenuItem
                disabled={bulkAutoMatchMutation.isPending}
                onClick={() => {
                  if (confirm(`Auto-match ${totalUnmatched} unmatched Mercury deposit${totalUnmatched === 1 ? "" : "s"} to PadSplit earnings rows? Only unambiguous matches (amount within 1%, date within 7 days) will be applied.`)) {
                    bulkAutoMatchMutation.mutate();
                  }
                }}
                data-testid="button-bulk-auto-match"
              >
                {bulkAutoMatchMutation.isPending ? "Matching…" : `Auto-Match All (${totalUnmatched})`}
              </DropdownMenuItem>
            )}
            {canUndo && lastAutoMatch && (
              <DropdownMenuItem
                disabled={undoAutoMatchMutation.isPending}
                onClick={() => {
                  if (confirm(`Undo the last auto-match run? This will unmatch ${lastAutoMatch.depositIds.length} deposit${lastAutoMatch.depositIds.length === 1 ? "" : "s"}.`)) {
                    undoAutoMatchMutation.mutate(lastAutoMatch.depositIds);
                  }
                }}
                data-testid="button-undo-bulk-auto-match"
              >
                <Undo2 className="h-4 w-4 mr-2" />
                {undoAutoMatchMutation.isPending
                  ? "Undoing…"
                  : `Undo last auto-match (${lastAutoMatch.depositIds.length}) · ${Math.floor(undoSecondsLeft / 60)}:${String(undoSecondsLeft % 60).padStart(2, "0")}`}
              </DropdownMenuItem>
            )}
          </>
        }
      />

      {filterImportBatchId != null && (
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 rounded-full border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-2 py-0.5 text-xs text-green-700 dark:text-green-300"
            data-testid="badge-active-import-batch-filter"
          >
            <FileArchive className="h-3 w-3" />
            <span>Filtered to import #{filterImportBatchId}</span>
            <button
              onClick={() => setFilterImportBatchId(null)}
              className="ml-1 text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100"
              title="Clear import batch filter"
              data-testid="button-clear-import-batch-filter"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10">
                  <Checkbox
                    checked={displayedIncomeRows.length > 0 && displayedIncomeRows.every((r) => selectedIncomeIds.has(r.id))}
                    onCheckedChange={(checked) => {
                      if (checked) setSelectedIncomeIds(new Set(displayedIncomeRows.map((r) => r.id)));
                      else setSelectedIncomeIds(new Set());
                    }}
                    data-testid="checkbox-income-select-all"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Property</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Account</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              )}
              {!isLoading && displayedIncomeRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    {rows.length === 0 ? (
                      <>
                        <p className="text-gray-500 mb-2" data-testid="text-no-income">No income entries found</p>
                        <p className="text-sm text-gray-400">Import PadSplit earnings or connect Mercury to see credits here, or add income manually.</p>
                      </>
                    ) : (
                      <p className="text-gray-400" data-testid="text-no-income-search">No transactions match your search.</p>
                    )}
                  </td>
                </tr>
              )}
              {displayedIncomeRows.map((row) => {
                // Task #2305 — split children for this income row
                const rowIncomeChildren = incomeSplitChildrenMap.get(row.id) ?? [];
                const isIncomeSplitParent = incomeSplitParentIds.has(row.id);
                const isExcluded = deriveReviewStatus(row) === "excluded";
                return (
                <Fragment key={row.id}>
                <tr className={`hover:bg-gray-50 transition-colors${isExcluded ? " opacity-60" : ""}`} data-testid={`row-income-${row.id}`}>
                  {/* Shift-click range selection — the td captures shiftKey and
                      delegates to useRangeSelection; the Checkbox itself is inert. */}
                  <td
                    className="px-3 py-3 w-10 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); handleIncomeRowClick(row.id, e.shiftKey); }}
                  >
                    <Checkbox
                      checked={selectedIncomeIds.has(row.id)}
                      onCheckedChange={() => {}}
                      data-testid={`checkbox-income-row-${row.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap" data-testid={`text-income-date-${row.id}`}>{row.date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {incomeSourceBadge(row.source)}
                      {isExcluded && (
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 flex items-center gap-0.5">
                          <EyeOff className="h-2.5 w-2.5" />
                          Excluded
                        </span>
                      )}
                      {/* Task #2332 — Split badge on parent rows in the Income table.
                          Mirrors the expense-side badge tooltip wording so hosts
                          understand the parent shows the full bank amount while
                          children carry the per-category / per-property breakdown. */}
                      {isIncomeSplitParent && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center gap-0.5 cursor-help"
                                data-testid={`badge-income-split-${row.id}`}
                              >
                                <Scissors className="h-2.5 w-2.5" />
                                Split
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs max-w-[260px]">
                                This row shows the full bank amount. The per-category and per-property breakdown is on the child rows below.
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px]">
                    {/* QBO-style categorize-in-place: searchable inline Property editor.
                        The single-row PATCH stamps reviewedAt server-side, so the row
                        leaves For review once a property is set. */}
                    <InlineCellEditor
                      value={row.isCompanyLevel ? "__company__" : row.propertyId ? String(row.propertyId) : ""}
                      placeholder="No property"
                      options={incomePropertyOptions}
                      onSelect={(v) => {
                        if (v === "__company__") {
                          updateIncomeMutation.mutate({ id: row.id, data: { isCompanyLevel: true, propertyId: null } });
                        } else {
                          updateIncomeMutation.mutate({ id: row.id, data: { isCompanyLevel: false, propertyId: v === "none" ? null : Number(v) } });
                        }
                      }}
                      testId={`select-income-property-${row.id}`}
                      disabled={updateIncomeMutation.isPending}
                    />
                  </td>
                  {/* Task #3788 — Mercury account (last 4) column. */}
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap" data-testid={`text-income-account-${row.id}`}>
                    {formatMercuryAccountLabel(row.accountNickname, row.accountLast4) ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`text-income-category-${row.id}`}>
                    {isIncomeSplitParent ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <InlineCellEditor
                        value={row.category || ""}
                        placeholder="No category"
                        options={incomeCategoryOptions}
                        onSelect={(v) => updateIncomeMutation.mutate({ id: row.id, data: { category: v === "none" ? null : v } })}
                        testId={`select-income-category-${row.id}`}
                        disabled={updateIncomeMutation.isPending}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-[200px]">
                    <div className="truncate">{row.vendor ? highlightMatch(row.vendor, incomeSearchText) : row.description ? highlightMatch(row.description, incomeSearchText) : <span className="text-gray-400">—</span>}</div>
                    {row.description && row.vendor && <div className="text-xs text-gray-400 truncate">{highlightMatch(row.description, incomeSearchText)}</div>}
                    {row.source === "padsplit_earnings" && (() => {
                      const bd = parsePadsplitBreakdown(row.notes);
                      if (!bd) return null;
                      return (
                        <div className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                          Dues: ${bd.dues.toFixed(2)}
                          {bd.lateFees !== 0 && <> · Late: ${bd.lateFees.toFixed(2)}</>}
                          {bd.bookingFees !== 0 && <> · Booking: −${bd.bookingFees.toFixed(2)}</>}
                          {bd.serviceFees !== 0 && <> · Service: −${bd.serviceFees.toFixed(2)}</>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap" data-testid={`text-income-amount-${row.id}`}>
                    ${parseFloat(row.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3" data-testid={`text-income-status-${row.id}`}>{reconciliationStatusBadge(row.reconciliationStatus)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Revenue triage — one-click "Mark reviewed" on unreviewed rows. */}
                      {deriveReviewStatus(row) === "for_review" && (
                        <button
                          onClick={() => bulkSetReviewedMutation.mutate({ ids: [row.id], reviewed: true })}
                          disabled={bulkSetReviewedMutation.isPending}
                          className="p-1.5 rounded-md text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors disabled:opacity-50"
                          title="Mark reviewed"
                          aria-label="Mark reviewed"
                          data-testid={`button-income-mark-reviewed-${row.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      )}
                      {/* Secondary row actions collapsed into a ⋯ menu. */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            aria-label="Row actions"
                            data-testid={`button-income-row-menu-${row.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {row.source === "mercury_credit" && row.reconciliationStatus === "unmatched" && (
                            <DropdownMenuItem
                              onClick={() => { setSelectedDeposit(row); setSelectedEarningsIds(new Set()); setShowMatchPanel(true); }}
                              data-testid={`button-income-match-${row.id}`}
                            >
                              Match to PadSplit earnings
                            </DropdownMenuItem>
                          )}
                          {row.source === "mercury_credit" && row.reconciliationStatus !== "unmatched" && (
                            <DropdownMenuItem
                              onClick={() => unmatchMutation.mutate(row.id)}
                              disabled={unmatchMutation.isPending}
                              data-testid={`button-income-unmatch-${row.id}`}
                            >
                              Unmatch deposit
                            </DropdownMenuItem>
                          )}
                          {row.source === "manual" && (
                            <DropdownMenuItem onClick={() => openEdit(row)} data-testid={`button-income-edit-${row.id}`}>
                              Edit
                            </DropdownMenuItem>
                          )}
                          {/* Task #2305 — Split for imported rows (opens split editor only) */}
                          {row.source !== "manual" && (
                            <DropdownMenuItem onClick={() => openEdit(row)} data-testid={`button-income-split-${row.id}`}>
                              <Scissors className="h-3.5 w-3.5 mr-2" />
                              Split into line items
                            </DropdownMenuItem>
                          )}
                          {!isExcluded ? (
                            <DropdownMenuItem
                              onClick={() => excludeIncomeMutation.mutate(row.id)}
                              disabled={excludeIncomeMutation.isPending}
                              data-testid={`button-income-exclude-${row.id}`}
                            >
                              <EyeOff className="h-3.5 w-3.5 mr-2" />
                              Exclude from P&L
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => unexcludeIncomeMutation.mutate(row.id)}
                              disabled={unexcludeIncomeMutation.isPending}
                              data-testid={`button-income-restore-${row.id}`}
                            >
                              <Eye className="h-3.5 w-3.5 mr-2" />
                              Restore to P&L
                            </DropdownMenuItem>
                          )}
                          {row.source === "manual" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => { if (confirm("Delete this income entry?")) deleteMutation.mutate(row.id); }}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`button-income-delete-${row.id}`}
                              >
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                {/* Task #2305 — indented child rows for split income */}
                {rowIncomeChildren.map((child) => (
                  <tr key={child.id} className="bg-indigo-50/30 dark:bg-indigo-950/10 border-l-4 border-indigo-200 dark:border-indigo-800" data-testid={`row-income-child-${child.id}`}>
                    <td className="px-3 py-2 w-10" />
                    <td className="px-4 py-2 pl-10 text-xs text-gray-500 whitespace-nowrap">{child.date}</td>
                    <td className="px-4 py-2">{incomeSourceBadge(child.source)}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 max-w-[140px] truncate">
                      {child.propertyId ? (propertyMap[child.propertyId] || `Property #${child.propertyId}`) : <span className="text-gray-400">—</span>}
                    </td>
                    {/* Task #3788 — account column placeholder for split children
                        (the account lives on the parent bank row). */}
                    <td className="px-4 py-2 text-xs text-gray-400">—</td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        {allIncomeCategories.find((c) => c.value === child.category)?.label ?? child.category}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 max-w-[160px] truncate">
                      {child.notes || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-medium text-gray-700">
                      ${parseFloat(child.amount || "0").toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-2">{reconciliationStatusBadge(child.reconciliationStatus)}</td>
                    <td className="px-4 py-2">
                      {row.source === "manual" && (
                        <button
                          onClick={() => openEdit(row)}
                          className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                          data-testid={`button-income-edit-split-child-${child.id}`}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-sm text-gray-600">
            <span>{rows.length} {rows.length === 1 ? "entry" : "entries"}</span>
            <span className="font-semibold">
              {/* Task #2305 — exclude split children from income total to avoid double-counting */}
              Total: ${rows.filter((r) => (r as any).splitParentId == null).reduce((s, r) => {
                const children = incomeSplitChildrenMap.get(r.id);
                if (children && children.length > 0) {
                  return s + children.reduce((cs, c) => cs + parseFloat(c.amount || "0"), 0);
                }
                return s + parseFloat(r.amount || "0");
              }, 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>

      {/* Add/Edit income modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="modal-add-income">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold">{editingRow ? "Edit Income" : "Add Income"}</h3>
              <button onClick={() => { setShowAddModal(false); resetAddForm(); }} className="text-gray-400 hover:text-gray-600" data-testid="button-income-modal-close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Task #2305 — for imported/non-manual rows, main fields are read-only; only the split editor is actionable */}
            {editingRow && editingRow.source !== "manual" && (
              <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="banner-imported-readonly">
                This row was imported and cannot be edited directly. Use the Split toggle below to allocate it across properties or categories.
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property (optional)</label>
                <select
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500"
                  value={addForm.propertyId}
                  onChange={(e) => setAddForm((f) => ({ ...f, propertyId: e.target.value }))}
                  disabled={!!(editingRow && editingRow.source !== "manual")}
                  data-testid="select-income-property"
                >
                  <option value="">No property</option>
                  {/* Task #3788 — pinned company-level option (non-property). */}
                  <option value="__company__">Company Operations</option>
                  {properties
                    .filter((p) => p.status !== "archive")
                    .map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.address}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500"
                  value={addForm.category}
                  onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))}
                  disabled={!!(editingRow && editingRow.source !== "manual")}
                  data-testid="select-income-category"
                >
                  {allIncomeCategories.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    value={addForm.amount}
                    onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                    disabled={!!(editingRow && editingRow.source !== "manual")}
                    placeholder="0.00"
                    data-testid="input-income-amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    value={addForm.date}
                    onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                    disabled={!!(editingRow && editingRow.source !== "manual")}
                    data-testid="input-income-date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor / Payer (optional)</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  value={addForm.vendor}
                  onChange={(e) => setAddForm((f) => ({ ...f, vendor: e.target.value }))}
                  disabled={!!(editingRow && editingRow.source !== "manual")}
                  placeholder="e.g. PadSplit, tenant name…"
                  data-testid="input-income-vendor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  value={addForm.description}
                  onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  disabled={!!(editingRow && editingRow.source !== "manual")}
                  placeholder="Brief description"
                  data-testid="input-income-description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm resize-none disabled:bg-gray-50 disabled:text-gray-500"
                  value={addForm.notes}
                  onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                  disabled={!!(editingRow && editingRow.source !== "manual")}
                  placeholder="Any additional notes…"
                  data-testid="input-income-notes"
                />
              </div>

              {/* Task #2305 — Income split editor (edit mode only) */}
              {editingRow && (
                <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <Scissors className="h-3.5 w-3.5 text-indigo-500" />
                        Split Transaction
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">Break into multiple line items with different properties or categories</p>
                    </div>
                    <Switch
                      checked={incomeSplitMode}
                      onCheckedChange={(v) => {
                        setIncomeSplitMode(v);
                        if (v && incomeSplitLines.length === 0) {
                          const half = (parseFloat(addForm.amount || "0") / 2).toFixed(2);
                          // Task #3788 — line 1 inherits the parent's property OR the
                          // "__company__" sentinel; the picker offers a Company
                          // Operations option and submit normalizes the sentinel.
                          setIncomeSplitLines([
                            { propertyId: addForm.propertyId, category: addForm.category, amount: half, notes: "" },
                            { propertyId: "", category: addForm.category, amount: half, notes: "" },
                          ]);
                        } else if (!v) {
                          setIncomeSplitLines([]);
                        }
                      }}
                      data-testid="switch-income-split-mode"
                    />
                  </div>
                  {incomeSplitMode && (
                    <div className="space-y-2">
                      {/* Task #2305 — inline blocking error message */}
                      {incomeSplitError && (
                        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 font-medium" data-testid="text-income-split-error">
                          {incomeSplitError}
                        </div>
                      )}
                      {(() => {
                        const total = incomeSplitLines.reduce((s, l) => s + parseFloat(l.amount || "0"), 0);
                        const parentAmt = parseFloat(addForm.amount || "0");
                        const diff = parentAmt - total;
                        const balanced = Math.abs(diff) < 0.01;
                        return (
                          <div className={`text-xs flex justify-between px-1 ${balanced ? "text-green-600" : "text-amber-600"}`}>
                            <span>Lines total: ${total.toFixed(2)} / ${parentAmt.toFixed(2)}</span>
                            <span>{balanced ? "✓ Balanced" : diff > 0 ? `$${diff.toFixed(2)} remaining` : `$${(-diff).toFixed(2)} over`}</span>
                          </div>
                        );
                      })()}
                      {incomeSplitLines.map((line, idx) => (
                        <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-2 bg-gray-50 dark:bg-gray-900/50">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500">Line {idx + 1}</span>
                            {incomeSplitLines.length > 2 && (
                              <button
                                type="button"
                                onClick={() => setIncomeSplitLines((prev) => prev.filter((_, i) => i !== idx))}
                                className="text-red-400 hover:text-red-600 text-xs"
                                data-testid={`button-remove-income-split-line-${idx}`}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={line.propertyId || "__none"} onValueChange={(v) => setIncomeSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, propertyId: v === "__none" ? "" : v } : l))}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`select-income-split-property-${idx}`}>
                                <SelectValue placeholder="Property (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">No property</SelectItem>
                                <SelectItem value="__company__">Company Operations</SelectItem>
                                {properties.map((p) => (
                                  <SelectItem key={p.id} value={String(p.id)}>{p.address || `Property #${p.id}`}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={line.category} onValueChange={(v) => setIncomeSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, category: v } : l))}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`select-income-split-category-${idx}`}>
                                <SelectValue placeholder="Category *" />
                              </SelectTrigger>
                              <SelectContent>
                                {allIncomeCategories.map((c) => (
                                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              placeholder="Amount *"
                              value={line.amount}
                              onChange={(e) => setIncomeSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, amount: e.target.value } : l))}
                              className="h-8 text-xs border border-gray-200 rounded-md px-2"
                              data-testid={`input-income-split-amount-${idx}`}
                            />
                            <input
                              placeholder="Notes (optional)"
                              value={line.notes}
                              onChange={(e) => setIncomeSplitLines((prev) => prev.map((l, i) => i === idx ? { ...l, notes: e.target.value } : l))}
                              className="h-8 text-xs border border-gray-200 rounded-md px-2"
                              data-testid={`input-income-split-notes-${idx}`}
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setIncomeSplitLines((prev) => [...prev, { propertyId: "", category: addForm.category || "other_income", amount: "", notes: "" }])}
                        className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                        data-testid="button-add-income-split-line"
                      >
                        + Add another line
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => { setShowAddModal(false); resetAddForm(); }}
                className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                data-testid="button-income-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Task #2305 — pre-validate split balance before submitting
                  if (incomeSplitMode && editingRow) {
                    setIncomeSplitError(null);
                    if (incomeSplitLines.length < 2) { setIncomeSplitError("A split requires at least 2 lines."); return; }
                    for (const l of incomeSplitLines) {
                      if (!l.category || !l.amount) { setIncomeSplitError("Each split line must have a category and amount."); return; }
                    }
                    const parentAmt = parseFloat(addForm.amount || "0");
                    const total = incomeSplitLines.reduce((s, l) => s + parseFloat(l.amount || "0"), 0);
                    if (Math.abs(total - parentAmt) > 0.01) {
                      setIncomeSplitError(`Splits must total $${parentAmt.toFixed(2)}, currently $${total.toFixed(2)}.`); return;
                    }
                  }
                  // Task #2314 — confirm before discarding existing split line
                  // items when the host toggles Split OFF and saves. Use the
                  // authoritative server-side child count (the cached income
                  // list is filtered by property/date and may undercount). If
                  // the count query hasn't resolved yet, prompt pessimistically
                  // so we never silently delete children.
                  if (!incomeSplitMode && editingRow) {
                    if (editingChildCountUnresolved) {
                      // Should not happen — button is disabled while loading —
                      // but guard anyway.
                      return;
                    }
                    if (editingActualChildCount > 0) {
                      setIncomeUnsplitConfirm({ childCount: editingActualChildCount });
                      return;
                    }
                  }
                  addMutation.mutate(addForm);
                }}
                disabled={addMutation.isPending || upsertIncomeSplitMutation.isPending || deleteIncomeSplitMutation.isPending || editingChildCountUnresolved || (!editingRow && (!addForm.amount || !addForm.date))}
                className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                data-testid="button-income-modal-save"
              >
                {(addMutation.isPending || upsertIncomeSplitMutation.isPending) ? "Saving…" : editingRow ? "Save Changes" : "Add Income"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Match / Reconciliation panel */}
      {showMatchPanel && selectedDeposit && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" data-testid="panel-income-match">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold">Match Mercury Deposit</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Deposit on {selectedDeposit.date} — <span className="font-medium text-gray-700">${parseFloat(selectedDeposit.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  {selectedDeposit.vendor && ` from ${selectedDeposit.vendor}`}
                </p>
              </div>
              <button onClick={() => { setShowMatchPanel(false); setSelectedDeposit(null); setSelectedEarningsIds(new Set()); }} className="text-gray-400 hover:text-gray-600" data-testid="button-match-panel-close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Earnings rows */}
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Select PadSplit earnings rows to match:
                {suggestLoading && <span className="ml-2 text-gray-400 text-xs">Finding suggestions…</span>}
              </p>
              {matchPanelEarnings.length === 0 && !suggestLoading && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No unmatched PadSplit earnings rows in the ±45-day window. Import PadSplit earnings first.
                </div>
              )}
              <div className="space-y-2">
                {matchPanelEarnings.map((r) => {
                  const checked = selectedEarningsIds.has(r.id);
                  const scored = r as any;
                  const isSplitParent = incomeSplitParentIds.has(r.id) || scored.hasSplits === true;
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`label-earnings-row-${r.id}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded text-blue-600"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedEarningsIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            return next;
                          });
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-gray-800">${parseFloat(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                            {/* Task #2329 — Split badge on parent earnings rows */}
                            {isSplitParent && (
                              <span
                                className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center gap-0.5"
                                title="This earnings row is split into multiple categories. Categories appear on the child rows."
                                data-testid={`badge-split-match-earnings-${r.id}`}
                              >
                                <Scissors className="h-2.5 w-2.5" />
                                Split
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{r.date}</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{r.description || r.vendor || "PadSplit earnings"}</p>
                        {r.propertyId && <p className="text-xs text-blue-600">{propertyMap[r.propertyId] || `Property #${r.propertyId}`}</p>}
                        {scored.score != null && <span className="text-xs text-gray-400">Match score: {scored.score}</span>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 pt-4 border-t border-gray-100">
              {selectedEarningsIds.size > 0 && (
                <div className="mb-3 text-sm text-gray-600 flex items-center justify-between">
                  <span>Selected earnings total:</span>
                  <span className="font-semibold">
                    ${[...selectedEarningsIds].reduce((s, id) => {
                      const r = rows.find((x) => x.id === id) || matchPanelEarnings.find((x) => x.id === id);
                      return s + (r ? parseFloat(r.amount) : 0);
                    }, 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowMatchPanel(false); setSelectedDeposit(null); setSelectedEarningsIds(new Set()); }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
                  data-testid="button-match-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => matchMutation.mutate({ depositId: selectedDeposit.id, earningsIds: [...selectedEarningsIds] })}
                  disabled={selectedEarningsIds.size === 0 || matchMutation.isPending}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  data-testid="button-match-confirm"
                >
                  {matchMutation.isPending ? "Matching…" : `Match (${selectedEarningsIds.size} selected)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task #2314 — Confirm before discarding existing income split lines
          when the host toggles Split OFF and saves. */}
      <AlertDialog open={!!incomeUnsplitConfirm} onOpenChange={(open) => { if (!open) setIncomeUnsplitConfirm(null); }}>
        <AlertDialogContent data-testid="dialog-confirm-unsplit-income">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove split line items?</AlertDialogTitle>
            <AlertDialogDescription>
              {incomeUnsplitConfirm
                ? `This will permanently remove ${incomeUnsplitConfirm.childCount} split ${incomeUnsplitConfirm.childCount === 1 ? "line" : "lines"} from this income entry. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-unsplit-income-cancel">Keep split</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIncomeUnsplitConfirm(null);
                addMutation.mutate(addForm);
              }}
              data-testid="button-unsplit-income-confirm"
            >
              Remove split lines
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revenue triage — sticky bulk-action bar (renders nothing at 0 selected).
          "Select all N" is exact: the ledger is client-filtered, so
          displayedIncomeRows IS the full set matching the current filters. */}
      <BulkBar
        count={selectedIncomeIds.size}
        totalMatching={displayedIncomeRows.length}
        onSelectAllMatching={() => setSelectedIncomeIds(new Set(displayedIncomeIds))}
        onClear={() => setSelectedIncomeIds(new Set())}
      >
        <Select
          value=""
          disabled={bulkSetPropertyMutation.isPending}
          onValueChange={(v) =>
            bulkSetPropertyMutation.mutate({
              ids: Array.from(selectedIncomeIds),
              propertyValue: v === "__company__" ? "__company__" : v === "none" ? null : Number(v),
            })
          }
        >
          <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="select-bulk-income-property">
            <SelectValue placeholder={bulkSetPropertyMutation.isPending ? "Setting…" : "Set property"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No property</SelectItem>
            <SelectItem value="__company__">Company Operations</SelectItem>
            {sortPropertiesParentFirst(properties.filter((p) => p.status !== "archive") as any).map((p: any) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.address || `Property #${p.id}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value=""
          disabled={bulkSetCategoryMutation.isPending}
          onValueChange={(v) =>
            bulkSetCategoryMutation.mutate({
              ids: Array.from(selectedIncomeIds),
              category: v === "none" ? null : v,
            })
          }
        >
          <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="select-bulk-income-category">
            <SelectValue placeholder={bulkSetCategoryMutation.isPending ? "Setting…" : "Set category"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No category</SelectItem>
            {allIncomeCategories.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(statusFilter === "for_review" || statusFilter === "all") && (
          <Button
            size="sm"
            className="h-8 gap-1 bg-green-600 text-white hover:bg-green-700 whitespace-nowrap"
            disabled={bulkSetReviewedMutation.isPending}
            onClick={() => bulkSetReviewedMutation.mutate({ ids: Array.from(selectedIncomeIds), reviewed: true })}
            data-testid="button-bulk-mark-reviewed-income"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {bulkSetReviewedMutation.isPending ? "Saving…" : "Mark reviewed"}
          </Button>
        )}
        {statusFilter === "categorized" && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 whitespace-nowrap"
            disabled={bulkSetReviewedMutation.isPending}
            onClick={() => bulkSetReviewedMutation.mutate({ ids: Array.from(selectedIncomeIds), reviewed: false })}
            data-testid="button-bulk-mark-unreviewed-income"
          >
            {bulkSetReviewedMutation.isPending ? "Saving…" : "Mark unreviewed"}
          </Button>
        )}
        {statusFilter !== "excluded" && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1 text-gray-600 whitespace-nowrap"
            disabled={bulkExcludeIncomeMutation.isPending || bulkUnexcludeIncomeMutation.isPending}
            onClick={() => bulkExcludeIncomeMutation.mutate(Array.from(selectedIncomeIds))}
            data-testid="button-bulk-exclude-income"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Exclude
          </Button>
        )}
        {(statusFilter === "excluded" || statusFilter === "all") && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1 text-green-700 whitespace-nowrap"
            disabled={bulkExcludeIncomeMutation.isPending || bulkUnexcludeIncomeMutation.isPending}
            onClick={() => bulkUnexcludeIncomeMutation.mutate(Array.from(selectedIncomeIds))}
            data-testid="button-bulk-restore-income"
          >
            <Eye className="h-3.5 w-3.5" />
            Restore
          </Button>
        )}
      </BulkBar>
    </div>
  );
}

// ─── Forwarded Docs Tab ──────────────────────────────────────────────────────

